import { Box, Flex, Tag, Text } from '@chakra-ui/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import cx from 'clsx';
import { useMemo } from 'react';

import { EditorJsRenderer } from '#features/blog/components/EditorJsRenderer';
import { ReadingProgressBar } from '#features/blog/components/ReadingProgressBar';
import { TableOfContents, type TocItem } from '#features/blog/components/TableOfContents';
import { useTrackBlogView } from '#features/blog/hooks/useTrackBlogView';
import { blogPostBySlugQueryOptions } from '#features/blog/model/blog';
import { getReadingTime } from '#features/blog/utils/getReadingTime';
import { useFormatDate } from '#features/date';
import { sharingOptions } from '#features/sharing/constants/sharingOptions';
import { Image, Link } from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { envConfig } from '#constants/envConfig';
import styles from './BlogPost.module.css';

import './wysiwyg-globals.css';

import { useParams } from '@tanstack/react-router';

const parseEditorJsContent = (content: string) => {
  try {
    const parsed = JSON.parse(content);
    if (parsed && Array.isArray(parsed.blocks)) {
      return parsed;
    }
  } catch {
    // Not JSON — legacy HTML content
  }
  return null;
};

const stripHtml = (html: string): string => html.replaceAll(/<[^>]*>/g, '');

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replaceAll(/[^a-zа-яё0-9\s-]/gi, '')
    .replaceAll(/\s+/g, '-')
    .replaceAll(/-+/g, '-')
    .slice(0, 60);

const extractTocItems = (content: string): TocItem[] => {
  const editorJs = parseEditorJsContent(content);
  if (!editorJs) return [];

  return editorJs.blocks
    .filter(
      (block: { type: string; data: Record<string, unknown> }) =>
        block.type === 'header' &&
        ((block.data.level as number) === 2 || (block.data.level as number) === 3)
    )
    .map((block: { type: string; data: Record<string, unknown> }, index: number) => {
      const text = stripHtml((block.data.text as string) ?? '');
      return {
        id: `heading-${index}-${slugify(text)}`,
        text,
        level: block.data.level as number,
      };
    });
};

export const BlogPost = () => {
  const { slug } = useParams({ from: '/{-$locale}/_layout/blog/$slug' });
  const { data: post } = useSuspenseQuery(blogPostBySlugQueryOptions(slug));
  const { _ } = useLingui();
  const formatDate = useFormatDate();

  const { date, title, content, ogTitle, previewImage, tags } = post;

  // Counts a view once the article is actually rendered for a reader in the browser.
  useTrackBlogView(post.id);

  const postLink = `${envConfig.HOST}/blog/${slug}?utm_source=share&utm_medium=link`;
  const linkTitle = `Teamplay.gg - ${ogTitle} `;

  const readingTime = useMemo(() => getReadingTime(content), [content]);
  const tocItems = useMemo(() => extractTocItems(content), [content]);

  return (
    <>
      <ReadingProgressBar />
      <Box maxW="1100px" w="100%" px={{ base: 4, md: 6 }}>
        {/* Breadcrumb */}
        <Flex gap={2} alignItems="center" mb={6} textStyle="sm" color="whiteAlpha.500">
          <Link to="/{-$locale}/blog" _hover={{ color: 'whiteAlpha.800' }}>
            {_(msg({ id: 'blog.breadcrumb.blog', message: 'Blog' }))}
          </Link>
          <Text>/</Text>
          {tags && (
            <>
              <Link
                to="/{-$locale}/blog"
                search={{ tag: tags.split(',')[0] }}
                color="whiteAlpha.600"
                _hover={{ color: 'whiteAlpha.800' }}
              >
                {tags.split(',')[0]}
              </Link>
              <Text>/</Text>
            </>
          )}
          <Text color="whiteAlpha.700" lineClamp={1}>
            {title}
          </Text>
        </Flex>

        {/* Title */}
        <Text
          as="h1"
          fontSize={{ base: '3xl', md: '5xl' }}
          fontWeight="800"
          lineHeight={{ base: '1.2', md: '1.15' }}
          mb={4}
        >
          {title}
        </Text>

        {/* Meta row: tags + date + reading time */}
        <Flex gap={{ base: 3, md: 4 }} alignItems="center" flexWrap="wrap" mb={8}>
          {tags && (
            <Flex gap={2} flexWrap="wrap">
              {tags.split(',').map((tag) => (
                <Tag.Root key={tag} size="md">
                  <Tag.Label textStyle="label">{tag}</Tag.Label>
                </Tag.Root>
              ))}
            </Flex>
          )}
          {date && (
            <Text color="whiteAlpha.500" textStyle="sm">
              {formatDate(date, 'dd MMMM yyyy')}
            </Text>
          )}
          <Text color="whiteAlpha.500" textStyle="sm">
            ·
          </Text>
          <Text color="whiteAlpha.500" textStyle="sm">
            {readingTime} {_(msg({ id: 'blog.readingTime', message: 'min read' }))}
          </Text>
        </Flex>

        {/* Hero image */}
        <Box w="100%" aspectRatio="16 / 9" borderRadius="xl" overflow="hidden" mb={10}>
          <Image
            src={previewImage}
            alt={title}
            width={1100}
            style={{
              objectFit: 'cover',
              objectPosition: 'top center',
              width: '100%',
              height: '100%',
            }}
          />
        </Box>

        {/* Content + Table of Contents */}
        <Flex gap={10} alignItems="flex-start">
          {/* Main content */}
          <Box flex={1} minW={0} maxW="720px">
            {(() => {
              const editorJs = parseEditorJsContent(content);
              if (editorJs) {
                return <EditorJsRenderer content={editorJs} tocItems={tocItems} />;
              }
              return (
                <Box
                  className={cx(styles.content, 'ql-editor')}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              );
            })()}

            {/* Share section */}
            <Box mt={16} mb={10} pt={8} borderTopWidth="1px" borderColor="whiteAlpha.100">
              <Text fontWeight="600" textStyle="lg" mb={4} color="whiteAlpha.700">
                {_(msg({ id: 'blog.share', message: 'Share this article' }))}
              </Text>
              <Flex gap={2}>
                {sharingOptions.map(({ icon, name, url }) => (
                  <IconButton
                    key={name}
                    variant="ghost"
                    p={0}
                    borderRadius="full"
                    fontSize="36px"
                    _icon={{ width: '36px', height: '36px' }}
                    _hover={{ bg: 'whiteAlpha.100' }}
                    asChild
                  >
                    <a href={url(postLink, linkTitle)} target="_blank" rel="noopener noreferrer">
                      {icon}
                    </a>
                  </IconButton>
                ))}
              </Flex>
            </Box>

            {/* Back to blog */}
            <Box mb={10}>
              <Link
                to="/{-$locale}/blog"
                textStyle="title"
                color="purple.400"
                _hover={{ color: 'purple.300' }}
              >
                ← {_(msg({ id: 'blog.backToBlog', message: 'Back to all articles' }))}
              </Link>
            </Box>
          </Box>

          {/* Table of Contents sidebar */}
          <TableOfContents items={tocItems} />
        </Flex>
      </Box>
    </>
  );
};

BlogPost.getLayout = null;
